import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HomeHero from "../components/home/HomeHero";
import HomeFeatures from "../components/home/HomeFeatures";
import HomeCTA from "../components/home/HomeCTA";
import SeoHead from "../components/seo/SeoHead";
import MarketingPageShell from "../components/layout/MarketingPageShell";
import useThemeMode from "../hooks/useThemeMode";
import useI18n from "../i18n/useI18n";

const HOOKORA_HOME_PROMO_DISMISSED_KEY = "hookora_home_promo_dismissed_until";

function HomePage() {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const [promoOpen, setPromoOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const featuredGuides = useMemo(
    () => [
      {
        title: tt("home.guides.whatIsHookora.title", "What Is Hookora?"),
        description: tt(
          "home.guides.whatIsHookora.description",
          "Learn what Hookora is, how it helps creators move from idea to stronger content faster, and why it now goes beyond basic hook generation."
        ),
        to: "/blog/what-is-hookora",
      },
      {
        title: tt("home.guides.nigerianCreators.title", "Hookora for Nigerian Creators"),
        description: tt(
          "home.guides.nigerianCreators.description",
          "See how Hookora helps Nigerian creators build stronger hooks, titles, captions, scripts, localized rewrites, and better content packaging."
        ),
        to: "/blog/hookora-for-nigerian-creators",
      },
      {
        title: tt(
          "home.guides.writeHooksFaster.title",
          "How Hookora Helps Creators Write Hooks Faster"
        ),
        description: tt(
          "home.guides.writeHooksFaster.description",
          "Explore how Hookora helps creators write hooks faster, find sharper angles, improve retention, and turn more ideas into publish-ready content."
        ),
        to: "/blog/how-hookora-helps-creators-write-hooks-faster",
      },
    ],
    [t]
  );

  const colors = {
    sectionBg: isDark ? "#020617" : "#f8fafc",
    cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
    cardSoftBg: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
    border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
    borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
    text: isDark ? "#f8fafc" : "#111827",
    textSoft: isDark ? "#cbd5e1" : "#374151",
    textMuted: isDark ? "#94a3b8" : "#6b7280",
    badgeBg: isDark ? "rgba(109,40,217,0.18)" : "#f5f3ff",
    badgeText: isDark ? "#d8b4fe" : "#6d28d9",
    shadow: isDark
      ? "0 14px 40px rgba(2, 6, 23, 0.45)"
      : "0 12px 30px rgba(15, 23, 42, 0.06)",
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const token = localStorage.getItem("access_token");
    if (token) return undefined;

    const dismissedUntil = localStorage.getItem(HOOKORA_HOME_PROMO_DISMISSED_KEY);
    const now = Date.now();

    if (dismissedUntil && Number(dismissedUntil) > now) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setPromoOpen(true);
    }, 1800);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const originalOverflow = document.body.style.overflow;

    if (promoOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [promoOpen]);

  const dismissPromo = () => {
    if (typeof window !== "undefined") {
      const dismissForDays = 5;
      const until = Date.now() + dismissForDays * 24 * 60 * 60 * 1000;
      localStorage.setItem(HOOKORA_HOME_PROMO_DISMISSED_KEY, String(until));
    }
    setPromoOpen(false);
  };

  const promoStyles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: isDark ? "rgba(2,6,23,0.72)" : "rgba(15,23,42,0.45)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        padding: isMobile ? "12px" : "20px",
        overflowY: "auto",
      },
      modal: {
        width: "min(720px, 100%)",
        maxHeight: isMobile ? "calc(100vh - 24px)" : "calc(100vh - 40px)",
        marginTop: isMobile ? "0" : "auto",
        marginBottom: isMobile ? "0" : "auto",
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb"}`,
        borderRadius: isMobile ? "20px" : "28px",
        boxShadow: isDark
          ? "0 28px 80px rgba(2,6,23,0.6)"
          : "0 28px 80px rgba(15,23,42,0.18)",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      },
      header: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        padding: isMobile ? "18px 18px 8px" : "24px 24px 8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "16px",
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      },
      closeButton: {
        border: `1px solid ${isDark ? "rgba(148,163,184,0.22)" : "#d1d5db"}`,
        background: isDark ? "rgba(30,41,59,0.9)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#111827",
        borderRadius: "12px",
        width: "40px",
        height: "40px",
        cursor: "pointer",
        fontSize: "18px",
        fontWeight: 700,
        flexShrink: 0,
        position: "sticky",
        top: 0,
      },
      badge: {
        display: "inline-flex",
        padding: "8px 14px",
        borderRadius: "999px",
        background: isDark ? "rgba(59,130,246,0.16)" : "#dbeafe",
        color: isDark ? "#bfdbfe" : "#1d4ed8",
        fontWeight: 800,
        fontSize: "12px",
        letterSpacing: "0.02em",
        marginBottom: "14px",
      },
      body: {
        padding: isMobile ? "0 18px 18px" : "0 24px 24px",
      },
      title: {
        margin: "0 0 12px",
        color: isDark ? "#f8fafc" : "#111827",
        fontSize: "clamp(1.7rem, 4vw, 2.8rem)",
        lineHeight: "1.05",
        letterSpacing: "-0.03em",
      },
      subtitle: {
        margin: "0 0 18px",
        color: isDark ? "#cbd5e1" : "#374151",
        fontSize: isMobile ? "15px" : "16px",
        lineHeight: "1.75",
        maxWidth: "620px",
      },
      featureGrid: {
        display: "grid",
        gridTemplateColumns: isMobile
          ? "1fr"
          : "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "12px",
        marginBottom: "18px",
      },
      featureCard: {
        background: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb"}`,
        borderRadius: "16px",
        padding: "14px",
      },
      featureTitle: {
        margin: "0 0 6px",
        color: isDark ? "#f8fafc" : "#111827",
        fontSize: "14px",
        fontWeight: 800,
      },
      featureText: {
        margin: 0,
        color: isDark ? "#94a3b8" : "#6b7280",
        fontSize: "13px",
        lineHeight: "1.6",
      },
      offerStrip: {
        marginTop: "6px",
        marginBottom: "18px",
        padding: "14px 16px",
        borderRadius: "18px",
        background: isDark
          ? "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.18))"
          : "linear-gradient(135deg, #eff6ff, #f5f3ff)",
        border: `1px solid ${isDark ? "rgba(129,140,248,0.24)" : "#c7d2fe"}`,
      },
      offerTitle: {
        margin: "0 0 6px",
        color: isDark ? "#f8fafc" : "#111827",
        fontSize: "15px",
        fontWeight: 800,
      },
      offerText: {
        margin: 0,
        color: isDark ? "#cbd5e1" : "#374151",
        fontSize: "14px",
        lineHeight: "1.65",
      },
      metricsRow: {
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
        gap: "10px",
        marginBottom: "20px",
      },
      metricCard: {
        background: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb"}`,
        borderRadius: "16px",
        padding: "14px",
        textAlign: "center",
      },
      metricValue: {
        margin: "0 0 4px",
        color: isDark ? "#f8fafc" : "#111827",
        fontSize: "24px",
        fontWeight: 900,
      },
      metricLabel: {
        margin: 0,
        color: isDark ? "#94a3b8" : "#6b7280",
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
      },
      ctaRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        flexDirection: isMobile ? "column" : "row",
      },
      primaryCta: {
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        textDecoration: "none",
        background: "linear-gradient(135deg, #2563eb, #7c3aed)",
        color: "#ffffff",
        padding: "14px 18px",
        borderRadius: "14px",
        fontWeight: 800,
        boxShadow: "0 12px 28px rgba(79, 70, 229, 0.24)",
      },
      secondaryCta: {
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        textDecoration: "none",
        background: isDark ? "rgba(30,41,59,0.92)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#111827",
        padding: "14px 18px",
        borderRadius: "14px",
        fontWeight: 800,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.2)" : "#d1d5db"}`,
      },
      subtleText: {
        marginTop: "12px",
        color: isDark ? "#94a3b8" : "#6b7280",
        fontSize: "12px",
        lineHeight: "1.5",
      },
    }),
    [isDark, isMobile]
  );

  return (
    <MarketingPageShell>
      <SeoHead
        title={tt("home.seoTitle", "Hookora | AI Content Workflow Platform for Creators")}
        description={tt(
          "home.seoDescription",
          "Hookora helps creators generate hooks, titles, captions, scripts, rewrites, retention ideas, and stronger content assets from one polished workflow."
        )}
        path="/"
      />

      <HomeHero />
      <HomeFeatures />

      <section style={{ padding: "8px 20px 56px", background: colors.sectionBg }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "8px 14px",
                borderRadius: "999px",
                background: colors.badgeBg,
                color: colors.badgeText,
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              {tt("home.featuredGuidesBadge", "Featured Hookora Guides")}
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                color: colors.text,
                fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
                lineHeight: "1.1",
              }}
            >
              {tt("home.learnSectionTitle", "Learn how Hookora helps creators move faster")}
            </h2>

            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                fontSize: "16px",
                lineHeight: "1.75",
                maxWidth: "760px",
                marginInline: "auto",
              }}
            >
              {tt(
                "home.learnSectionDescription",
                "Explore quick guides about what Hookora is, who it helps, and how it makes hook writing, content ideation, rewrites, packaging, and creator workflow feel clearer and faster."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "18px",
            }}
          >
            {featuredGuides.map((guide) => (
              <article
                key={guide.to}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "24px",
                  padding: "24px",
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px",
                    color: colors.text,
                    fontSize: "24px",
                    lineHeight: "1.25",
                  }}
                >
                  {guide.title}
                </h3>

                <p
                  style={{
                    margin: "0 0 18px",
                    color: colors.textSoft,
                    fontSize: "15px",
                    lineHeight: "1.75",
                  }}
                >
                  {guide.description}
                </p>

                <Link
                  to={guide.to}
                  style={{
                    display: "inline-flex",
                    textDecoration: "none",
                    background: colors.cardSoftBg,
                    color: colors.text,
                    padding: "12px 16px",
                    borderRadius: "12px",
                    fontWeight: "800",
                    border: `1px solid ${colors.borderStrong}`,
                  }}
                >
                  {tt("common.readGuide", "Read Guide")}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <HomeCTA />

      {promoOpen && (
        <div style={promoStyles.overlay} onClick={dismissPromo}>
          <div style={promoStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={promoStyles.header}>
              <div>
                <div style={promoStyles.badge}>
                  {tt("home.promo.badge", "Creator Workflow Spotlight")}
                </div>
              </div>

              <button
                type="button"
                onClick={dismissPromo}
                aria-label={tt("home.promo.closeLabel", "Close Hookora promo")}
                style={promoStyles.closeButton}
              >
                ✕
              </button>
            </div>

            <div style={promoStyles.body}>
              <h2 style={promoStyles.title}>
                {tt("home.promo.title", "Create stronger content faster with Hookora")}
              </h2>

              <p style={promoStyles.subtitle}>
                {tt(
                  "home.promo.subtitle",
                  "Hookora helps creators generate hooks, titles, captions, scripts, rewrites, retention ideas, and stronger content assets from one polished workflow built to improve both speed and output quality."
                )}
              </p>

              <div style={promoStyles.featureGrid}>
                <div style={promoStyles.featureCard}>
                  <h3 style={promoStyles.featureTitle}>
                    {tt("home.promo.features.writeFasterTitle", "Write faster")}
                  </h3>
                  <p style={promoStyles.featureText}>
                    {tt(
                      "home.promo.features.writeFasterText",
                      "Move from rough idea to stronger copy without staring at a blank page."
                    )}
                  </p>
                </div>

                <div style={promoStyles.featureCard}>
                  <h3 style={promoStyles.featureTitle}>
                    {tt("home.promo.features.packageBetterTitle", "Package better")}
                  </h3>
                  <p style={promoStyles.featureText}>
                    {tt(
                      "home.promo.features.packageBetterText",
                      "Improve hooks, titles, rewrites, retention, and overall content positioning."
                    )}
                  </p>
                </div>

                <div style={promoStyles.featureCard}>
                  <h3 style={promoStyles.featureTitle}>
                    {tt("home.promo.features.upgradeSmarterTitle", "Upgrade smarter")}
                  </h3>
                  <p style={promoStyles.featureText}>
                    {tt(
                      "home.promo.features.upgradeSmarterText",
                      "Unlock deeper Studio tools designed for serious creators and growth."
                    )}
                  </p>
                </div>
              </div>

              <div style={promoStyles.offerStrip}>
                <h3 style={promoStyles.offerTitle}>
                  {tt("home.promo.offerTitle", "Yearly plans now hit harder")}
                </h3>
                <p style={promoStyles.offerText}>
                  {tt("home.promo.offerTextPrefix", "Save")}{" "}
                  <strong>{tt("pricing.save17", "Save 17%")}</strong>{" "}
                  {tt("home.promo.offerTextMiddle", "on yearly Creator and Pro plans and get the feel of")}{" "}
                  <strong>{tt("pricing.twoMonthsFree", "2 months free")}</strong>{" "}
                  {tt("home.promo.offerTextSuffix", "while unlocking Hookora’s full premium workflow.")}
                </p>
              </div>

              <div style={promoStyles.metricsRow}>
                <div style={promoStyles.metricCard}>
                  <p style={promoStyles.metricValue}>22</p>
                  <p style={promoStyles.metricLabel}>
                    {tt("home.promo.metrics.studioTools", "Studio Tools")}
                  </p>
                </div>
                <div style={promoStyles.metricCard}>
                  <p style={promoStyles.metricValue}>1</p>
                  <p style={promoStyles.metricLabel}>
                    {tt("home.promo.metrics.cleanerWorkflow", "Cleaner Workflow")}
                  </p>
                </div>
                <div style={promoStyles.metricCard}>
                  <p style={promoStyles.metricValue}>17%</p>
                  <p style={promoStyles.metricLabel}>
                    {tt("home.promo.metrics.yearlySavings", "Yearly Savings")}
                  </p>
                </div>
              </div>

              <div style={promoStyles.ctaRow}>
                <Link to="/register" style={promoStyles.primaryCta} onClick={dismissPromo}>
                  {tt("pricing.startFree", "Start Free")}
                </Link>

                <Link to="/pricing" style={promoStyles.secondaryCta} onClick={dismissPromo}>
                  {tt("home.promo.seeYearlyPlans", "See Yearly Plans")}
                </Link>
              </div>

              <div style={promoStyles.subtleText}>
                {tt(
                  "home.promo.subtleText",
                  "Explore Hookora first, then upgrade when you want the full Studio power."
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </MarketingPageShell>
  );
}

export default HomePage;